/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

import { Link } from 'lib/i18n/routing'
import { StructuredText } from 'react-datocms/structured-text'
import { type StructuredTextDocument } from 'react-datocms'
import {
	BreadcrumbItemProps,
	Breadcrumbs,
	Text,
} from '@hashicorp/mds-react/components'
import { type LayoutFieldsFragment } from 'types/dato'
import Headshot, { type HeadshotProps } from 'components/materia/headshot'
import { Layout } from 'components/materia/layout'
import s from './style.module.css'

export interface ConferenceSpeakerBioProps {
	layout: LayoutFieldsFragment
	breadcrumbs: BreadcrumbItemProps[]
	headline: string
	bio: { value: StructuredTextDocument }
	headshot: HeadshotProps
	sessions: SessionProps[]
}

interface SessionProps {
	title: string
	link: string
	date: string
}

const ConferenceSpeakerBio = ({
	layout,
	breadcrumbs,
	headline,
	bio,
	headshot,
	sessions,
}: ConferenceSpeakerBioProps) => {
	return (
		<Layout layout={layout}>
			<div className={s.root}>
				<Breadcrumbs.Root className={s.breadcrumbs}>
					{breadcrumbs.map((item, idx) => (
						<Breadcrumbs.Item
							{...item}
							current={idx === breadcrumbs.length - 1}
							key={item.text}
						/>
					))}
				</Breadcrumbs.Root>
				<div className={s.headshotMobile}>
					<Headshot {...headshot} />
				</div>
				<div className={s.text}>
					<Text.DisplayExpressive
						tag={'h1'}
						color="strong"
						className={s.heading}
					>
						{headline}
					</Text.DisplayExpressive>
					<Text.Body color="primary" className={s.bio}>
						<StructuredText data={bio} />
					</Text.Body>
					{sessions && sessions.length ? (
						<ul className={s.sessions}>
							{sessions.map((session, index) => {
								return (
									<li key={index} className={s.session}>
										<Link href={session.link}>
											<Text.Body
												tag="p"
												color="strong"
												className={s.sessionTitle}
											>
												{session.title}
											</Text.Body>
											<Text.Body tag="p" color="faint">
												{session.date}
											</Text.Body>
										</Link>
									</li>
								)
							})}
						</ul>
					) : null}
				</div>
				<div className={s.headshot}>
					<Headshot {...headshot} />
				</div>
			</div>
		</Layout>
	)
}

export default ConferenceSpeakerBio
